import { calculateForumScore } from './forumScore';
import { getForumGrowthPercent } from './forumGrowth';
import type { ForumBase } from '../types/forum';

export type LeaderMetric = 'score' | 'members' | 'posts' | 'topics' | 'growth';

export type RankedForum = {
  forum: ForumBase;
  rank: number;
  value: number;
  score: number;
  growth: number | null;
  tier: 'S' | 'A' | 'B' | 'C';
};

export type CategoryChampion = {
  category: string;
  forum: ForumBase;
  value: number;
  growth: number | null;
};

export const LEADER_METRICS: { id: LeaderMetric; label: string; short: string }[] = [
  { id: 'score', label: 'Genel skor', short: 'Skor' },
  { id: 'members', label: 'Üye sayısı', short: 'Üye' },
  { id: 'posts', label: 'İleti sayısı', short: 'İleti' },
  { id: 'topics', label: 'Konu sayısı', short: 'Konu' },
  { id: 'growth', label: 'Üye büyümesi', short: 'Büyüme' },
];

export function getMetricValue(forum: ForumBase, metric: LeaderMetric): number {
  const score = calculateForumScore(
    forum.topics_count,
    forum.posts_count,
    forum.members_count,
    forum.founded_year
  );
  if (metric === 'score') return score;
  if (metric === 'members') return forum.members_count;
  if (metric === 'posts') return forum.posts_count;
  if (metric === 'topics') return forum.topics_count;
  const g = getForumGrowthPercent(forum, 'members');
  return g ?? -999;
}

export function getTier(rank: number): RankedForum['tier'] {
  if (rank <= 10) return 'S';
  if (rank <= 25) return 'A';
  if (rank <= 50) return 'B';
  return 'C';
}

export function rankForums(forums: ForumBase[], metric: LeaderMetric, limit = 50): RankedForum[] {
  const active = forums.filter((f) => f.is_active !== false);
  const rows = active
    .map((forum) => {
      const score = calculateForumScore(
        forum.topics_count,
        forum.posts_count,
        forum.members_count,
        forum.founded_year
      );
      const growth = getForumGrowthPercent(forum, 'members');
      const value = getMetricValue(forum, metric);
      return { forum, value, score, growth };
    })
    .filter((row) => (metric === 'growth' ? row.growth !== null && row.growth > 0 : true))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

  return rows.map((row, i) => ({
    ...row,
    rank: i + 1,
    tier: getTier(i + 1),
  }));
}

export function getCategoryChampions(forums: ForumBase[], metric: LeaderMetric): CategoryChampion[] {
  const active = forums.filter((f) => f.is_active !== false);
  const byCategory = new Map<string, ForumBase[]>();
  for (const f of active) {
    if (!f.category) continue;
    const list = byCategory.get(f.category) ?? [];
    list.push(f);
    byCategory.set(f.category, list);
  }

  const champions: CategoryChampion[] = [];
  for (const [category, list] of byCategory) {
    if (list.length < 2) continue;
    const ranked = rankForums(list, metric, 1);
    if (!ranked[0]) continue;
    champions.push({
      category,
      forum: ranked[0].forum,
      value: ranked[0].value,
      growth: ranked[0].growth,
    });
  }

  return champions.sort((a, b) => b.value - a.value).slice(0, 12);
}

export function formatLeaderValue(metric: LeaderMetric, value: number, growth: number | null): string {
  if (metric === 'growth' && growth !== null) return `+%${growth.toFixed(1)}`;
  return value.toLocaleString('tr-TR');
}

export function leagueStats(forums: ForumBase[]) {
  const active = forums.filter((f) => f.is_active !== false);
  const ranked = rankForums(active, 'score', 1);
  const withGrowth = active
    .map((f) => getForumGrowthPercent(f, 'members'))
    .filter((g): g is number => g !== null && g > 0);
  const avgGrowth =
    withGrowth.length > 0 ? withGrowth.reduce((s, g) => s + g, 0) / withGrowth.length : 0;
  const categories = new Set(active.map((f) => f.category).filter(Boolean)).size;

  return {
    total: active.length,
    categories,
    champion: ranked[0]?.forum ?? null,
    championScore: ranked[0]?.value ?? 0,
    avgGrowth,
    growingCount: withGrowth.length,
  };
}
